import React, { useContext } from "react";
import { cx } from "./util";
import { FormGroupContext, InputGroupContext } from "./formContext";

type FormProps = {
  inline?: boolean;
} & React.FormHTMLAttributes<HTMLFormElement>;

export function Form({ inline, className, children, ...rest }: FormProps) {
  return (
    <form
      className={
        cx(inline && "flex flex-wrap items-center gap-2", className) ||
        undefined
      }
      {...rest}
    >
      {children}
    </form>
  );
}

type FormGroupProps = {
  controlId?: string;
  validationState?: string;
  bsSize?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export function FormGroup({
  controlId,
  validationState,
  bsSize,
  className,
  children,
  ...rest
}: FormGroupProps) {
  return (
    <FormGroupContext.Provider value={{ controlId, validationState }}>
      <div className={cx("mb-3", className)} {...rest}>
        {children}
      </div>
    </FormGroupContext.Provider>
  );
}

export function ControlLabel({
  className,
  children,
  ...rest
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  const { controlId } = useContext(FormGroupContext);
  return (
    <label className={cx("label", className)} htmlFor={controlId} {...rest}>
      {children}
    </label>
  );
}

type ControlKind = "input" | "select" | "textarea";

const CONTROL_CLASS: Record<ControlKind, string> = {
  input: "input w-full",
  select: "select w-full",
  textarea: "textarea w-full",
};
const CONTROL_ERROR_CLASS: Record<ControlKind, string> = {
  input: "input-error",
  select: "select-error",
  textarea: "textarea-error",
};

type FormControlProps = {
  componentClass?: ControlKind;
} & React.AllHTMLAttributes<HTMLElement>;

function FormControlBase({
  componentClass,
  className,
  children,
  ...rest
}: FormControlProps) {
  const { controlId, validationState } = useContext(FormGroupContext);
  const joined = useContext(InputGroupContext);
  const kind = componentClass || "input";
  const Component = kind as React.ElementType;
  return (
    <Component
      id={controlId}
      className={cx(
        CONTROL_CLASS[kind],
        validationState === "error" && CONTROL_ERROR_CLASS[kind],
        joined && "join-item",
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
}

// daisy's input-error border replaces the bootstrap feedback glyph.
function FormControlFeedback() {
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components -- compound component (Object.assign) the rule can't recognize
export const FormControl = Object.assign(FormControlBase, {
  Feedback: FormControlFeedback,
});

export function HelpBlock({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cx("text-error text-sm mt-1", className)} {...rest}>
      {children}
    </p>
  );
}

type InputGroupProps = {
  bsSize?: string;
} & React.HTMLAttributes<HTMLDivElement>;

function InputGroupBase({
  bsSize,
  className,
  children,
  ...rest
}: InputGroupProps) {
  // bsSize is accepted but no longer changes sizing (2 call sites; the size
  // bump was cosmetic). Recorded as a known visual delta.
  return (
    <InputGroupContext.Provider value={true}>
      <div className={cx("join", className)} {...rest}>
        {children}
      </div>
    </InputGroupContext.Provider>
  );
}

const InputGroupAddon = ({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cx(
      "join-item flex items-center border border-base-300 bg-base-200 px-3 text-sm",
      className,
    )}
    {...rest}
  >
    {children}
  </span>
);

const InputGroupButton = ({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cx("join-item flex", className)} {...rest}>
    {children}
  </span>
);

// eslint-disable-next-line react-refresh/only-export-components -- compound component (Object.assign) the rule can't recognize
export const InputGroup = Object.assign(InputGroupBase, {
  Addon: InputGroupAddon,
  Button: InputGroupButton,
});

type CheckboxRadioProps = {
  inline?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export function Checkbox({
  inline,
  className,
  children,
  ...rest
}: CheckboxRadioProps) {
  return (
    <label
      className={cx(
        // NOT daisyUI's `.label` — that mutes its text to a grey that reads as
        // disabled (review #51). Plain flex keeps the caption at normal
        // contrast; `checkbox-primary` colours the checked tick in the theme
        // colour so a selected box is clearly distinct from a disabled one.
        "flex cursor-pointer items-center gap-2",
        inline && "inline-flex",
        className,
      )}
    >
      <input type="checkbox" className="checkbox checkbox-primary" {...rest} />{" "}
      {children}
    </label>
  );
}

export function Radio({
  inline,
  className,
  children,
  ...rest
}: CheckboxRadioProps) {
  const body = (
    <label
      className={cx(
        // See Checkbox above: avoid `.label` (greys the caption), colour the
        // selected dot with `radio-primary` (review #51).
        "flex cursor-pointer items-center gap-2",
        inline && "inline-flex",
      )}
    >
      <input type="radio" className="radio radio-primary" {...rest} />{" "}
      {children}
    </label>
  );
  return inline ? body : <div className={className || undefined}>{body}</div>;
}
